import React, { useEffect, useState } from 'react';
import type { CSSProperties, FormEvent, ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { Input, Button } from '../../components/ui';
import useStyles from '../../theme/useStyles';
import { publicRegistration } from '../../api/endpoints';
import type { RegistrationFormData, RegistrationLookup } from '../../api/endpoints';
import { ApiError } from '../../api/client';
import RegistrationFormFields, {
  emptyForm, validateRegistration, withKeys, DEFAULT_OPTIONS
} from './RegistrationFormFields';
import type { RegistrationOptions } from './RegistrationFormFields';
import RegistrationSummary from './RegistrationSummary';
import { printDocument } from '../../utils/printService';
import type { ChurchConfig } from '../../types';
import { registrationPrintData, REGISTRATION_PRINT_MODULE, REGISTRATION_PRINT_DOCUMENT } from './registrationPrint';

// The login-free family registration page, opened from the link the office
// shares (/churchManag/register/<token>). A family first identifies itself by the head's
// mobile and date of birth; what follows depends on what the server knows:
// a new form, their pending entry to edit, a rejection to correct, or the
// approved result, which can no longer be changed from here. Whatever the
// status, the family can view their registration and print it or save it as
// a PDF, in the layout designed under Settings › Print Templates. With the
// link switched off the page is simply unavailable.

type Stage = 'loading' | 'closed' | 'verify' | 'status' | 'form' | 'done' | 'view';

const formatWhen = (iso?: string) => {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
};

const PublicRegistration = () => {
  const { token = '' } = useParams();
  const s = useStyles(false);
  const t = s.t;

  const [stage, setStage] = useState<Stage>('loading');
  const [church, setChurch] = useState({ churchName: '', logo: '', address: '', contactNo: '' });
  // The parish's Member Status list, documents on/off and guest period.
  const [options, setOptions] = useState<RegistrationOptions>(DEFAULT_OPTIONS);
  const [mobile, setMobile] = useState('');
  const [dob, setDob] = useState('');
  const [result, setResult] = useState<RegistrationLookup | null>(null);
  const [form, setForm] = useState<RegistrationFormData>(emptyForm());
  const [website, setWebsite] = useState(''); // honeypot, never shown
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  // Where "Back" from View / Print returns to.
  const [viewFrom, setViewFrom] = useState<Stage>('status');

  useEffect(() => {
    let active = true;
    publicRegistration.info(token)
      .then((info) => {
        if (!active) return;
        setChurch({
          churchName: info.churchName, logo: info.logo, address: info.address || '', contactNo: info.contactNo || ''
        });
        setOptions({
          memberStatuses: info.memberStatuses?.length ? info.memberStatuses : DEFAULT_OPTIONS.memberStatuses,
          documentUploads: info.documentUploads !== false,
          guestMonths: Number(info.guestPeriodMonths) > 0 ? Number(info.guestPeriodMonths) : DEFAULT_OPTIONS.guestMonths
        });
        setStage('verify');
      })
      .catch(() => { if (active) setStage('closed'); });
    return () => { active = false; };
  }, [token]);

  useEffect(() => {
    if (church.churchName) document.title = `Family Registration · ${church.churchName}`;
  }, [church.churchName]);

  /** A closed link mid-session (switched off while filling in) shows the closed page. */
  const fail = (err: unknown) => {
    if (err instanceof ApiError && err.status === 404) { setStage('closed'); return; }
    setError((err as Error).message || 'Something went wrong. Please try again.');
  };

  const handleLookup = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    if (!/^\d{7,12}$/.test(mobile)) { setError('Enter the mobile number of the family head (7 to 12 digits).'); return; }
    if (!dob) { setError('Enter the date of birth of the family head.'); return; }
    setBusy(true);
    try {
      const found = await publicRegistration.lookup(token, mobile, dob);
      setResult(found);
      if (found.status === 'NEW') {
        setForm(emptyForm(mobile, dob));
        setStage('form');
      } else {
        if (found.form) setForm(withKeys(found.form));
        setStage('status');
      }
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    const problem = validateRegistration(form);
    if (problem) { setError(problem); window.scrollTo({ top: 0, behavior: 'smooth' }); return; }
    setBusy(true);
    try {
      // A new registration has nothing to unlock yet, so it sends no verification.
      const isEdit = result && result.status !== 'NEW' && result.status !== 'ALREADY_MEMBER';
      const saved = await publicRegistration.submit(
        token, isEdit ? mobile : '', isEdit ? dob : '', form, website
      );
      // The head's mobile or date of birth may have just been corrected; the
      // next edit must be unlocked with the new pair.
      setMobile(form.head.mobileNumber || mobile);
      setDob(form.head.dateOfBirth || dob);
      setResult(saved);
      setStage('done');
      window.scrollTo({ top: 0 });
    } catch (err) {
      fail(err);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setBusy(false);
    }
  };

  /**
   * Prints in the parish's "Family Registration Form" layout (Settings › Print
   * Templates), fetched through the public link. Without one set up, the page's
   * own view prints instead, so a family is never left unable to print.
   */
  const printRegistration = async () => {
    if (!result) return;
    setBusy(true);
    setError('');
    try {
      const template = await publicRegistration.printTemplate(token);
      if (!template) { window.print(); return; }
      // The letterhead comes from the public link, not the signed-in settings.
      const config = {
        churchName: church.churchName, address: church.address, contactNo: church.contactNo, logoBase64: church.logo
      } as ChurchConfig;
      await printDocument({
        module: REGISTRATION_PRINT_MODULE,
        documentType: REGISTRATION_PRINT_DOCUMENT,
        data: registrationPrintData(result, result.form || form),
        template,
        config,
        title: `Family-Registration-${result.referenceNo || ''}`
      });
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const openView = () => { setViewFrom(stage); setError(''); setStage('view'); window.scrollTo({ top: 0 }); };

  const startOver = () => {
    setResult(null); setError(''); setMobile(''); setDob(''); setForm(emptyForm()); setStage('verify');
  };

  // --- layout -------------------------------------------------------------

  const page: CSSProperties = {
    minHeight: '100vh', backgroundColor: t.bgApp, padding: '24px 16px 48px', boxSizing: 'border-box',
    fontFamily: 'inherit'
  };
  const shell: CSSProperties = {
    maxWidth: stage === 'form' ? '900px' : stage === 'view' ? '820px' : '520px', margin: '0 auto', backgroundColor: t.bgMain,
    borderRadius: t.radius.xl, boxShadow: t.shadowRaised, borderTop: `6px solid ${t.primary}`,
    padding: '24px', boxSizing: 'border-box'
  };
  const banner = (tone: 'info' | 'success' | 'warning' | 'error', children: ReactNode) => {
    const colour = { info: t.info, success: t.success, warning: t.warning, error: t.error }[tone];
    return (
      <div
        role={tone === 'error' ? 'alert' : 'status'}
        style={{
          borderLeft: `4px solid ${colour}`, backgroundColor: t.bgSubtle, padding: '12px 14px',
          borderRadius: t.radius.lg, color: t.textMain, fontSize: t.fontSize.md, marginBottom: '16px',
          lineHeight: 1.5
        }}
      >
        {children}
      </div>
    );
  };

  const header = (
    <div style={{ textAlign: 'center', marginBottom: '20px' }}>
      {church.logo && (
        <img src={church.logo} alt="" style={{ maxHeight: '72px', maxWidth: '160px', objectFit: 'contain', marginBottom: '8px' }} />
      )}
      {church.churchName && (
        <div style={{ color: t.textMuted, fontSize: t.fontSize.md, fontWeight: t.fontWeight.bold }}>{church.churchName}</div>
      )}
      <h1 style={{ margin: '4px 0 0', color: t.textMain, fontSize: t.fontSize.display }}>Family Registration</h1>
    </div>
  );

  const refLine = result?.referenceNo && (
    <div style={{ fontSize: t.fontSize.md, color: t.textMuted, marginBottom: '14px' }}>
      Reference No: <strong style={{ color: t.textMain, letterSpacing: '0.5px' }}>{result.referenceNo}</strong>
      {result.submittedAt && <> · Submitted {formatWhen(result.submittedAt)}</>}
      {result.updatedAt && result.updatedAt !== result.submittedAt && <> · Updated {formatWhen(result.updatedAt)}</>}
    </div>
  );

  const renderBody = () => {
    switch (stage) {
      case 'loading':
        return <p style={{ textAlign: 'center', color: t.textMuted }}>Loading…</p>;

      case 'closed':
        return (
          <div style={{ textAlign: 'center', padding: '12px 0' }}>
            <div style={{ fontSize: '44px' }} aria-hidden="true">🔒</div>
            <h1 style={{ color: t.textMain, fontSize: t.fontSize.xxl, margin: '8px 0' }}>Currently unavailable</h1>
            <p style={{ color: t.textMuted, fontSize: t.fontSize.md, lineHeight: 1.6 }}>
              Family registration is not available at the moment.
              Please try again later or contact the church office.
            </p>
          </div>
        );

      case 'verify':
        return (
          <form onSubmit={handleLookup} noValidate>
            {header}
            <p style={{ color: t.textMuted, fontSize: t.fontSize.md, lineHeight: 1.6, marginTop: 0 }}>
              Enter the <strong>family head's</strong> mobile number and date of birth.
              New families can register; families who already submitted can check the status, make changes, or print it.
            </p>
            {error && banner('error', error)}
            <div style={{ display: 'grid', gap: '12px' }}>
              <Input
                label="Mobile number of family head" required value={mobile} inputMode="numeric"
                placeholder="7 to 12 digits" autoComplete="tel"
                onChange={(e) => setMobile(e.target.value.replace(/\D/g, '').slice(0, 12))}
              />
              <Input
                label="Date of birth of family head" type="date" required value={dob}
                max={new Date().toISOString().slice(0, 10)}
                onChange={(e) => setDob(e.target.value)}
              />
            </div>
            <Button type="submit" fullWidth disabled={busy} style={{ marginTop: '18px' }}>
              {busy ? 'Checking…' : 'Continue →'}
            </Button>
          </form>
        );

      case 'status': {
        if (!result) return null;
        if (result.status === 'ALREADY_MEMBER') {
          return (
            <>
              {header}
              {banner('info', <>This mobile number is <strong>already registered</strong> with the church.
                For any changes, please contact the church office.</>)}
              <Button variant="secondary" fullWidth onClick={startOver}>← Use a different number</Button>
            </>
          );
        }
        if (result.status === 'APPROVED') {
          return (
            <>
              {header}
              {banner('success', <>✅ <strong>Approved.</strong> The family of {result.headName} is registered with the church.</>)}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginBottom: '14px' }}>
                {[['Family No.', result.familyId], ['Membership No.', result.membershipNo]].map(([label, v]) => (
                  <div key={label} style={{ border: `1px solid ${t.border}`, borderRadius: t.radius.lg, padding: '10px 12px' }}>
                    <div style={{ color: t.textMuted, fontSize: t.fontSize.sm }}>{label}</div>
                    <div style={{ color: t.textMain, fontWeight: t.fontWeight.black, fontSize: t.fontSize.lg }}>{v || '—'}</div>
                  </div>
                ))}
              </div>
              {refLine}
              <p style={{ color: t.textMuted, fontSize: t.fontSize.md }}>
                Approved registrations can no longer be edited here. Please contact the church office for any changes.
              </p>
              <Button fullWidth onClick={openView}>📄 View / Print (PDF)</Button>
              <Button variant="ghost" fullWidth onClick={startOver} style={{ marginTop: '8px' }}>← Done</Button>
            </>
          );
        }
        const rejected = result.status === 'REJECTED';
        const canEdit = result.canEdit !== false; // false only once approved
        return (
          <>
            {header}
            {rejected
              ? banner('error', <>❌ <strong>Not approved.</strong> Reason: {result.rejectReason || '—'}
                  {canEdit && <><br />Please correct the details and submit again.</>}</>)
              : banner('warning', <>⏳ <strong>Waiting for approval.</strong> The church office has not reviewed your
                  registration yet.{canEdit && ' You can still make changes.'}</>)}
            {refLine}
            <div style={{ color: t.textMain, fontSize: t.fontSize.md, marginBottom: '16px' }}>
              <strong>{result.form?.head.name}</strong> and {(result.form?.members.length || 0)} other member(s)
            </div>
            {canEdit && (
              <Button fullWidth onClick={() => { setError(''); setStage('form'); }}>
                ✏️ {rejected ? 'Correct and resubmit' : 'Edit details'}
              </Button>
            )}
            <Button variant={canEdit ? 'secondary' : 'primary'} fullWidth onClick={openView} style={{ marginTop: '8px' }}>📄 View / Print (PDF)</Button>
            <Button variant="ghost" fullWidth onClick={startOver} style={{ marginTop: '8px' }}>← Back</Button>
          </>
        );
      }

      case 'form':
        return (
          <form onSubmit={handleSubmit} noValidate>
            {header}
            {result?.status === 'PENDING' && banner('warning', 'You are editing a registration that is waiting for approval.')}
            {result?.status === 'REJECTED' && banner('error', <>Reason it was not approved: {result.rejectReason || '—'}</>)}
            {error && banner('error', error)}
            {/* Honeypot: hidden from people, tempting to bots. */}
            <input
              type="text" name="website" value={website} onChange={(e) => setWebsite(e.target.value)}
              tabIndex={-1} autoComplete="off" aria-hidden="true"
              style={{ position: 'absolute', left: '-10000px', width: '1px', height: '1px', opacity: 0 }}
            />
            <RegistrationFormFields
              value={form} onChange={setForm} options={options}
              onFileError={(msg) => { setError(msg); window.scrollTo({ top: 0, behavior: 'smooth' }); }}
            />
            <div style={{
              display: 'flex', gap: '10px', justifyContent: 'flex-end', flexWrap: 'wrap',
              borderTop: `1px solid ${t.border}`, marginTop: '20px', paddingTop: '16px'
            }}>
              <Button type="button" variant="ghost" onClick={startOver} disabled={busy}>Cancel</Button>
              <Button type="submit" disabled={busy}>
                {busy ? 'Submitting…' : result && result.status !== 'NEW' ? '💾 Update registration' : '📨 Submit registration'}
              </Button>
            </div>
          </form>
        );

      case 'done':
        return (
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '48px' }} aria-hidden="true">🙏</div>
            <h1 style={{ color: t.textMain, fontSize: t.fontSize.xxl, margin: '8px 0' }}>Thank you!</h1>
            <p style={{ color: t.textMuted, fontSize: t.fontSize.md, lineHeight: 1.6 }}>
              Your family details were sent to the church office for approval.
              You can come back to this link any time, enter the same mobile number and date of birth,
              and check the status or make changes until it is approved.
            </p>
            {result?.referenceNo && (
              <div style={{
                display: 'inline-block', margin: '8px 0 18px', padding: '10px 18px', borderRadius: t.radius.lg,
                backgroundColor: t.primarySoft, color: t.textAccent, fontWeight: t.fontWeight.black,
                fontSize: t.fontSize.xl, letterSpacing: '1px'
              }}>
                {result.referenceNo}
              </div>
            )}
            <Button fullWidth onClick={openView}>📄 View / Print (PDF)</Button>
            <Button variant="secondary" fullWidth onClick={startOver} style={{ marginTop: '8px' }}>Done</Button>
          </div>
        );

      case 'view':
        if (!result) return null;
        return (
          <>
            <div className="no-print" style={{ display: 'flex', gap: '8px', flexWrap: 'wrap', justifyContent: 'space-between', marginBottom: '16px' }}>
              <Button variant="ghost" onClick={() => setStage(viewFrom)}>← Back</Button>
              <Button onClick={printRegistration} disabled={busy}>{busy ? 'Preparing…' : '🖨️ Print / Save as PDF'}</Button>
            </div>
            <p className="no-print" style={{ color: t.textMuted, fontSize: t.fontSize.sm, marginTop: 0 }}>
              To save a PDF, press <strong>Print / Save as PDF</strong> and choose <strong>Save as PDF</strong> as the printer.
            </p>
            <RegistrationSummary
              churchName={church.churchName} logo={church.logo} address={church.address} contactNo={church.contactNo}
              result={result} form={result.form || form}
            />
          </>
        );

      default:
        return null;
    }
  };

  return (
    <div style={page} className="reg-page">
      {/* Print only the registration: no page background, card shadow or buttons. */}
      <style>{`
        @media print {
          @page { margin: 12mm; }
          html, body { background: #FFFFFF !important; }
          /* Tighter rows on paper, so a typical family fits on two pages. */
          .registration-summary td { padding: 3px 8px !important; font-size: 12px !important; }
          .no-print { display: none !important; }
          .reg-page { background: #FFFFFF !important; padding: 0 !important; min-height: 0 !important; }
          .reg-shell { box-shadow: none !important; border: none !important; max-width: none !important; padding: 0 !important; }
        }
      `}</style>
      <div style={shell} className="reg-shell">{renderBody()}</div>
    </div>
  );
};

export default PublicRegistration;
